import {
  Button,
  Divider,
  Drawer,
  Form,
  Grid,
  Input,
  InputNumber,
  Space,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import { IDamagedProduct } from 'pages/damages/create';
import { useEffect, useState } from 'react';
import { InventoryLotWithVariant, getInventoryLotsByVariant } from 'services/inventory';

interface EditDamageProductDrawerProps {
  visible: boolean;
  editProduct: IDamagedProduct;
  warehouse: string | undefined;
  onClose: () => void;
  onSubmit: (editedProduct: IDamagedProduct) => void;
}

const { Text } = Typography;

export const EditDamageProductDrawer: React.FC<EditDamageProductDrawerProps> = (props) => {
  const [inventoryLots, setInventoryLots] = useState<InventoryLotWithVariant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const breakpoint = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  useEffect(() => {
    form.resetFields();
    setInventoryLots([]);

    (async () => {
      setIsLoading(true);
      try {
        if (!props.warehouse || !props.editProduct) return;

        let quantityString = '';
        const inventoryLotResponse = await getInventoryLotsByVariant(
          props.editProduct.productVariantId,
          props.warehouse
        );
        setInventoryLots(inventoryLotResponse);

        form.setFieldsValue({
          variant: `${props.editProduct.sku} - ${props.editProduct.productVariantName} ${quantityString}`,
        });
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsLoading(false);
    })();
  }, [props.editProduct, form, props.warehouse]);

  const onFinish = (values: IDamagedProduct) => {
    if (!props.editProduct) return;

    const newData = {
      ...props.editProduct,
      quantity: values.quantity,
      lossAmount: values.lossAmount,
    };

    const totalQuantity = inventoryLots.reduce((total, lot) => {
      total += lot.quantity;
      return total;
    }, 0);

    if (newData.quantity > totalQuantity || newData.quantity === 0) {
      form.setFields([
        {
          name: 'quantity',
          errors: [`Quantity needs to be between 1 and ${totalQuantity}`],
        },
      ]);
      return;
    }

    props.onSubmit(newData);
    form.resetFields();
    setInventoryLots([]);
  };

  const availableStock = (
    <ul>
      {inventoryLots.map((lot) => (
        <li key={lot.id}>
          <Text>
            {`${lot.quantity} units available | Bought @ BDT ${lot.unitPrice} on ${new Date(
              lot.lotEntryDate
            ).toLocaleDateString()}`}
          </Text>
        </li>
      ))}
    </ul>
  );

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = props.editProduct.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const customFields = props.editProduct.customFields ?? [];

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  return (
    <Drawer
      title="Edit Product"
      placement="right"
      width={breakpoint.sm ? '450px' : '100%'}
      visible={props.visible}
      onClose={props.onClose}
    >
      <Form
        layout="vertical"
        initialValues={{
          variant: `${props.editProduct.sku} - ${props.editProduct.productVariantName}`,
          quantity: props.editProduct.quantity,
          lossAmount: props.editProduct.lossAmount,
        }}
        form={form}
        onFinish={onFinish}
      >
        <Form.Item label="Product" name="variant">
          <Input disabled />
        </Form.Item>

        <Divider />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
            <Typography.Text code>{props.editProduct.productName}</Typography.Text>
          </div>

          {attributeElements}
          {customFieldElements}

          {readPurchasePrice?.can && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>PURCHASE PRICE:</Typography.Text>
              <Typography.Text>{props.editProduct.purchasePrice}</Typography.Text>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography.Text strong>SELLING PRICE:</Typography.Text>
            <Typography.Text>{props.editProduct.sellingPrice}</Typography.Text>
          </div>
        </div>
        <Divider />

        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Loss Amount"
          name="lossAmount"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} min={0} precision={2} />
        </Form.Item>

        <Space direction="vertical">
          <Text style={{ fontWeight: 'bolder' }}>* Available Stock</Text>
          {availableStock}
        </Space>

        <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
          <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
